import { z } from "zod";
import { notificationExternalMessageReceiptSchema } from "./notification-delivery-receipts.js";
import { problemDetailsSchema } from "./problem-details.js";
import { teamsTenantIdSchema } from "./teams.js";
export const teamsForwardScopeSchema = z.enum([
    "personal",
    "groupchat",
    "team",
    "unknown",
]);
export const teamsForwardPayloadSchema = z.object({
    v: z.literal(1),
    aud: z.literal("mlx-teams-forward"),
    jti: z.string().trim().min(9).max(128),
    iat: z.number().int(),
    exp: z.number().int(),
    teamsTenantId: teamsTenantIdSchema,
    conversationId: z.string().trim().min(1).max(256),
    activityId: z.string().trim().min(1).max(256),
    threadId: z.string().trim().min(1).max(1024),
    serviceUrl: z.string().trim().min(1).max(1024).optional(),
    replyToId: z.string().trim().min(1).max(256).optional(),
    scope: teamsForwardScopeSchema,
    text: z.string(),
    externalUserId: z.string().trim().min(1).max(256).optional(),
    externalUserName: z.string().trim().min(1).max(256).optional(),
});
export const teamsGatewayIngestInputSchema = z.object({
    envelope: z.string().trim().min(1),
});
const teamsGatewayImmediateReplyResponseSchema = z.object({
    replyText: z.string().trim().min(1),
});
const teamsGatewayAcceptedResponseSchema = z.object({
    accepted: z.literal(true),
    taskId: z.string().trim().min(1).max(128),
    runId: z.string().trim().min(1).max(128),
    deduped: z.boolean(),
});
export const teamsGatewayIngestResponseSchema = z.union([
    teamsGatewayImmediateReplyResponseSchema,
    teamsGatewayAcceptedResponseSchema,
]);
export const teamsGatewayReplyInputSchema = z.object({
    idempotencyKey: z.string().trim().min(1).max(200),
    threadId: z.string().trim().min(1).max(1024),
    taskId: z.string().trim().min(1).max(128),
    runId: z.string().trim().min(1).max(128),
    sessionId: z.string().trim().min(1).max(128),
    sessionUrl: z.string().trim().url(),
    status: z.enum(["completed", "failed"]),
    outputText: z.string().optional(),
    errorMessage: z.string().optional(),
}).strict();
export const teamsGatewayReplyResponseSchema = z.object({
    ok: z.literal(true),
    providerMessageId: z.string().trim().min(1).max(1_024),
}).strict();
// Public, user-safe progress stages for Microsoft Teams and Copilot. The
// gateway renders these enum values into fixed copy so runtime payloads,
// retrieved content, tool arguments, and model reasoning never cross this
// boundary.
export const teamsGatewayProgressStageSchema = z.enum([
    "preparing",
    "retrieving",
    "reviewing",
    "using_tools",
    "writing",
]);
export const teamsGatewayProgressInputSchema = z.object({
    threadId: z.string().trim().min(1).max(1024),
    runId: z.string().trim().min(1).max(128),
    sequence: z.number().int().nonnegative(),
    stage: teamsGatewayProgressStageSchema,
}).strict();
export const teamsGatewayProgressResponseSchema = z.object({
    ok: z.literal(true),
    applied: z.boolean(),
}).strict();
export const teamsGatewayMessageContentSchema = z.object({
    title: z.string().trim().min(1).max(200),
    digest: z.boolean(),
    items: z.array(z.object({
        headline: z.string().trim().min(1).max(300),
        attribution: z.string().trim().min(1).max(300).nullable(),
        detail: z.string().max(500).nullable(),
        url: z.string().trim().url().max(2_048),
    }).strict()).min(1).max(25),
    omittedCount: z.number().int().min(0),
}).strict();
export const teamsGatewayMessageInputSchema = z.object({
    idempotencyKey: z.string().trim().min(1).max(200),
    teamsTenantId: teamsTenantIdSchema,
    externalUserId: z.string().trim().min(1).max(256),
    content: teamsGatewayMessageContentSchema,
}).strict();
export const teamsGatewayMessageResponseSchema = notificationExternalMessageReceiptSchema;
export const teamsGatewayMessageDispatchNotStartedProblemType = "https://mlx.systems/problems/teams-message-dispatch-not-started";
export const teamsGatewayMessageDispatchNotStartedProblemSchema = problemDetailsSchema.extend({
    type: z.literal(teamsGatewayMessageDispatchNotStartedProblemType),
    status: z.literal(503),
});
export const claimTeamsGatewayInstanceInputSchema = z.object({
    code: z.string().trim().min(1).max(64),
});
export const claimTeamsGatewayInstanceResponseSchema = z.object({
    teamsTenantId: teamsTenantIdSchema,
});
